import { buildSectionSelector, normalizeSectionId, sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, onDocumentReady } from '@theme/utilities';

/**
 * Hydrates a section using the Section Rendering API preserving states.
 * Only updates elements with data-hydration-key attribute. This ensures that non-targeted nodes do not have their markup/state updated unnecessarily.
 *
 * @param {string} sectionId - The section ID to hydrate
 * @param {URL} [url] - The URL to render the section from
 */
async function hydrateSection(sectionId, url) {
  const normalizedId = normalizeSectionId(sectionId);
  const section = document.getElementById(buildSectionSelector(normalizedId));

  if (!section || section.dataset.hydrated === 'true') {
    return;
  }

  // 20/10/2026 (Atlas) : réponse parfois sans la section (réseau, navigateur Facebook) = une reprise après 2 s,
  // puis l'erreur remonte comme avant si elle persiste. L'en-tête rendu par le serveur reste affiché dans tous les cas.
  // 29/10/2026 (Atlas) : Shopify repond parfois 429 / 5xx en rafale (robots de verification des pubs) :
  // deux reprises espacees (2 s puis 5 s), puis abandon silencieux : l'en-tete rendu par le serveur reste valable.
  const delays = [0, 2000, 5000];
  for (let attempt = 0; attempt < delays.length; attempt++) {
    if (delays[attempt]) await new Promise((resolve) => setTimeout(resolve, delays[attempt]));
    try {
      await sectionRenderer.renderSection(normalizedId, { cache: false, url, mode: 'hydration' });
      section.dataset.hydrated = 'true';
      return;
    } catch (error) {
      if (attempt === delays.length - 1) {
        console.warn('[hydration] section non actualisee', normalizedId, error);
      }
    }
  }
}

/**
 * Hydrates a section using the Section Rendering API preserving states, when
 * the DOM is ready.
 *
 * @param {string} sectionId - The section ID to hydrate
 * @param {URL} [url] - The URL to render the section from
 */
export async function hydrate(sectionId, url) {
  onDocumentReady(() => {
    requestIdleCallback(() => hydrateSection(sectionId, url));
  });
}
